// src/pages/EcxLoginPage.tsx
import React, { useMemo, useState, useCallback } from "react";

export default function EcxLoginPage() {
  const [loading, setLoading] = useState(false);
  // コールバック側（EcxCallback）が sessionStorage に積んだエラー文言を初期表示する
  const [error, setError] = useState<string | null>(() => {
    try {
      const m = sessionStorage.getItem("ecx_login_error");
      if (m) sessionStorage.removeItem("ecx_login_error");
      return m || null;
    } catch {
      return null;
    }
  });

  // ★利用規約/個人情報の取扱い 同意
  const [agreed, setAgreed] = useState(false);

  // ★お知らせ
  const [noticeOpen, setNoticeOpen] = useState(true);
  const [noticeVisible, setNoticeVisible] = useState(false); // 任意：初回のみ表示する場合など

  // ===== 設定（ENV化推奨）=====
  // Viteなら import.meta.env.VITE_*** を使うのが定番。無ければ直書きfallback。
  const authBase = process.env?.REACT_APP_ECX_AUTH_BASE;
  const clientId = process.env?.REACT_APP_ECX_CLIENT_ID;

  const redirectUri = useMemo(() => `${window.location.origin}/ecx/callback`, []);

  // ===== PKCE / State =====
  const base64UrlEncode = (bytes: Uint8Array) => {
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  };

  const genState = () => {
    const b = new Uint8Array(32);
    crypto.getRandomValues(b);
    return base64UrlEncode(b);
  };

  const generatePKCE = async () => {
    // code_verifier: 43-128 chars required
    const random = new Uint8Array(32); // 32 bytes => base64url 43 chars（要件満たす）
    crypto.getRandomValues(random);
    const codeVerifier = base64UrlEncode(random);

    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest("SHA-256", data);
    const codeChallenge = base64UrlEncode(new Uint8Array(digest));

    return { codeVerifier, codeChallenge };
  };

  const startEcxLogin = useCallback(async () => {
    if (!agreed) {
      setError("ec vision株式会社の利用規約／個人情報の取扱い に同意してください。");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!authBase) {
        setError("ecx authBase（REACT_APP_ECX_AUTH_BASE）が未設定です。");
        setLoading(false);
        return;
      }
      if (!clientId) {
        setError("ecx client_id（REACT_APP_ECX_CLIENT_ID）が未設定です。");
        setLoading(false);
        return;
      }
      if (!window.crypto?.subtle) {
        setError("このブラウザはPKCE（crypto.subtle）に対応していません。");
        setLoading(false);
        return;
      }

      // どのテナントのログインか（共通判定がlocalStorageを見るなら合わせる）
      sessionStorage.setItem("login_tenant", "ecx");
      localStorage.setItem("selectedTenant", "ecx"); // ★追加：check-status/resolveCompany 対策

      // ★ workspace_public_id 検証用（callback側で使う）
      // すでに過去ログインで保存しているなら「期待値」として渡す
      const expectedWorkspacePublicId = localStorage.getItem("ecx_workspace_public_id") || "";
      sessionStorage.setItem("ecx_expected_workspace_public_id", expectedWorkspacePublicId);

      // state（CSRF対策）
      const state = (crypto as any)?.randomUUID?.() || genState();
      sessionStorage.setItem("ecx_oauth_state", state);

      // PKCE
      const { codeVerifier, codeChallenge } = await generatePKCE();
      sessionStorage.setItem("ecx_code_verifier", codeVerifier);

      // ブラウザ/拡張機能の影響で sessionStorage が失われるケースの保険
      localStorage.setItem("ecx_oauth_state_tmp", state);
      localStorage.setItem("ecx_code_verifier_tmp", codeVerifier);

      // callback側のデバッグ/整合性チェックに使えるので保存しておく（任意）
      sessionStorage.setItem("ecx_client_id", clientId);
      sessionStorage.setItem("ecx_redirect_uri", redirectUri);

      // authorize
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: "code",
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        state,
      });

      // ✅ 任意：前回認可済みworkspaceを自動選択（効かなければ無視されるだけ）
      params.set("prompt", "selected_workspace");

      window.location.href = `${authBase}/oauth2/authorize?${params.toString()}`;
    } catch (e) {
      console.error("ecxログイン開始エラー:", e);
      setError("ecxログインの開始に失敗しました。時間をおいて再度お試しください。");
      setLoading(false);
    }
  }, [agreed, authBase, clientId, redirectUri]);

  return (
    <div className="min-h-screen bg-slate-100 py-8 md:py-10">
      <div className="mx-auto w-full max-w-2xl px-4">
        <div className="mb-6 text-center">
          <div className="flex items-center justify-center mt-4">
            <img src="/logo.png" alt="利益Navi" className="h-16 rounded-xl object-cover" />
          </div>
        </div>

        {noticeVisible && (
          <div className="mb-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div
              className="flex cursor-pointer items-center justify-between border-b border-slate-200 px-4 py-3"
              onClick={() => setNoticeOpen((prev) => !prev)}
            >
              <div className="flex items-center gap-2">
                <svg className="h-4 w-4 text-slate-400" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1" />
                  <line x1="7" y1="6" x2="7" y2="10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                  <circle cx="7" cy="4.5" r="0.6" fill="currentColor" />
                </svg>
                <span className="text-xs font-semibold tracking-wide text-slate-500">お知らせ</span>
              </div>

              <div className="flex items-center gap-2">
                <svg
                  className={`h-4 w-4 text-slate-400 transition-transform ${noticeOpen ? "rotate-180" : ""}`}
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                >
                  <polyline
                    points="3,5 7,9 11,5"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setNoticeVisible(false);
                  }}
                  className="flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 text-[10px] text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  aria-label="お知らせを閉じる"
                >
                  ✕
                </button>
              </div>
            </div>

            {noticeOpen && (
              <div className="px-4 py-3">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-700" />
                      障害情報
                    </span>
                    <span className="truncate text-sm font-semibold text-slate-900">
                      ログイン時の動作に関するご報告をいただいております
                    </span>
                  </div>
                  <span className="shrink-0 text-[11px] text-slate-400">2026/04/07</span>
                </div>

                <div className="text-xs leading-7 text-slate-500">
                  一部のお客様より、認証後の画面遷移についてご不便をいただいているご報告を受け、現在確認・対応中でございます。復旧まで今しばらくお待ちいただけますと幸いです。
                </div>
              </div>
            )}
          </div>
        )}

        <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          {/* 利用規約 / 個人情報の取扱い 同意（追加：privacy-notice リンク） */}
          <label className="mb-4 flex items-start gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300"
              checked={agreed}
              onChange={(e) => {
                setAgreed(e.target.checked);
                if (error) setError(null);
              }}
            />
            <span>
              ec vision株式会社の{" "}
              <a
                href="https://ec-vision.co.jp/terms/"
                target="_blank"
                rel="noreferrer"
                className="text-slate-800 underline hover:text-slate-900"
              >
                利用規約
              </a>
              {"／"}
              <a
                href="https://ec-vision.co.jp/privacy-notice/"
                target="_blank"
                rel="noreferrer"
                className="text-slate-800 underline hover:text-slate-900"
              >
                個人情報の取扱い
              </a>{" "}
              に同意します
            </span>
          </label>

          <button
            type="button"
            onClick={startEcxLogin}
            disabled={loading || !agreed}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-80"
            title={!agreed ? "利用規約／個人情報の取扱い に同意してください" : undefined}
          >
            <span>{loading ? "リダイレクト中..." : "ecxでログイン"}</span>
          </button>

          <div className="my-4 h-px bg-slate-200" />

          <div>
            <div className="mb-3 flex items-center gap-2">
              <svg className="h-4 w-4 text-slate-400" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1" />
                <path
                  d="M5 5.5c0-1.1.9-2 2-2s2 .9 2 2c0 .8-.5 1.5-1.2 1.8L7 7.8V9"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
                <circle cx="7" cy="10.5" r="0.6" fill="currentColor" />
              </svg>
              <span className="text-xs font-semibold tracking-wide text-slate-500">お問い合わせ</span>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 shrink-0 rounded bg-stone-100 px-2 py-0.5 text-[11px] font-semibold text-stone-600">
                  フリー・ライト
                </span>
                <div className="text-xs leading-7 text-slate-500">
                  フリープラン、ライトプランをご利用の方は{" "}
                  <a
                    href="https://docs.google.com/forms/d/e/1FAIpQLSdrzL9WveXl_qdF3CwmYk6rFgrLDBfjbItdyuvT3pwrdVRveQ/viewform?usp=header"
                    target="_blank"
                    rel="noreferrer"
                    className="text-slate-800 underline hover:text-slate-900"
                  >
                    こちら
                  </a>
                  よりお問い合わせください。
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="mt-0.5 shrink-0 rounded bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                  スタンダード
                </span>
                <div className="text-xs leading-7 text-slate-500">
                  スタンダードプラン（トライアル利用を含む）をご利用の方は、連携しているチャットツールにてご連絡ください。
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 w-full rounded-[10px] border border-[#AFA9EC] border-l-4 border-l-[#534AB7] bg-white px-5 py-[1.125rem]">
          <div className="mb-3.5 flex items-center gap-2.5">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#534AB7]">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <circle cx="7" cy="7" r="5" />
                <line x1="7" y1="4.5" x2="7" y2="7" />
                <circle cx="7" cy="9.5" r=".4" fill="#fff" />
              </svg>
            </div>
            <div className="text-sm font-bold text-[#26215C]">初めてログインする方へ まず初期設定ガイドをご確認ください</div>
          </div>

          <div className="mb-4 text-[13px] leading-7 text-[#5F5E5A]">
            ご登録のプランに合わせた初期設定ガイドをご用意しています。ログイン後、設定を完了してからご利用ください。
          </div>

          <div className="space-y-2">
            <a
              href="https://ec-vision.co.jp/rieki-navi/setup/ecx_free/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between rounded-lg border border-[#AFA9EC] bg-[#EEEDFE] px-3.5 py-2.5 text-[13px] font-bold text-[#3C3489] transition hover:opacity-85"
            >
              <span>フリープランの初期設定ガイド</span>
              <span aria-hidden="true">↗</span>
            </a>

            <a
              href="https://ec-vision.co.jp/rieki-navi/setup/ecx_light/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between rounded-lg border border-[#5DCAA5] bg-[#E1F5EE] px-3.5 py-2.5 text-[13px] font-bold text-[#085041] transition hover:opacity-85"
            >
              <span>ライトプランの初期設定ガイド</span>
              <span aria-hidden="true">↗</span>
            </a>

            <a
              href="https://ec-vision.co.jp/rieki-navi/setup/ecx_standard/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between rounded-lg border border-[#534AB7] bg-[#534AB7] px-3.5 py-2.5 text-[13px] font-bold text-white transition hover:opacity-85"
            >
              <span>スタンダードプランの初期設定ガイド</span>
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}